"use client";
import React, { useRef, useEffect, useState } from "react";
import Image from "next/image";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import BottomCurves from "../curves/bottomCurves";
import { getStrapiMedia } from "@/utils/api-helpers";
import RevealElement from "../revealElement";
import AnimatedButton from "../ui/AnimatedButton";

gsap.registerPlugin(ScrollTrigger);

export default function ImageDescContainer({ data, className }: any) {
    const direction =
        data.componentName === "ImageLeftBanner" ? "left" : "right";
    const containerRef = useRef(null);
    const paragraphRef = useRef<HTMLParagraphElement>(null);
    const [isTall, setIsTall] = useState(false);

    useEffect(() => {
        const checkHeight = () => {
            if (paragraphRef.current) {
                const height = paragraphRef.current.scrollHeight;
                setIsTall(height > 250);
            }
        };

        checkHeight(); // Initial
        window.addEventListener("resize", checkHeight);
        return () => window.removeEventListener("resize", checkHeight);
    }, [data]);

    return (
        <section
            className={`relative ${
                data.hasGradient ? "bg-rainbow" : "bg-lightGrey"
            } ${
                data.isOverlap && direction === "right"
                    ? "pt-20 lg:pt-[3vw] md:pb-0"
                    : ""
            } ${
                data.isOverlap && direction === "left"
                    ? "pt-20 lg:pt-[12vw]"
                    : ""
            } ${
                data.isOverlap
                    ? "lg:pb-10 xl:pb-24 2xl:pb-40 3xl:pb-36"
                    : data.bottomSpacing
                      ? "pb-16 md:pb-[13.417vw] pt-20 lg:pt-[4vw]"
                      : "pb-16 pt-20 lg:pt-[4vw]"
            }`}
        >
            <div
                className={`flex min-h-[360px] item-stretch lg:items-center ${
                    direction === "left"
                        ? "flex-col-reverse lg:flex-row lg:gap-12 xl:gap-40"
                        : direction === "right"
                          ? "flex-col-reverse lg:flex-row-reverse lg:gap-24 xl:gap-44"
                          : ""
                }`}
                ref={containerRef}
            >
                <div className="lg:w-full">
                    {data.image && (
                        <RevealElement>
                            <Image
                                src={getStrapiMedia(data.image.url)}
                                width={640}
                                height={790}
                                alt={data.image.alternativeText || ""}
                                className={`m-auto h-auto object-contain ${isTall ? "scale-110 md:scale-100 lg:scale-[160%] 2xl:scale-[175%] 3xl:scale-[150%]" : "scale-100 md:scale-100 lg:scale-[130%]"} imageChild ${className}`}
                                sizes="(max-width: 1024px) 100%, 100%"
                                loading="lazy"
                                style={{ overflowY: "initial" }}
                            />
                        </RevealElement>
                    )}
                </div>
                <div
                    className={`flex flex-col justify-center lg:w-[150%] ${
                        data.isOverlap ? " lg:-mt-[10vw]" : ""
                    }`}
                >
                    {data.header && (
                        <h2
                            className={`${
                                data.headerColor === "purple"
                                    ? "text-purple"
                                    : "text-darkGrey"
                            } textChild md:max-w-[90%]`}
                        >
                            {data.header}
                        </h2>
                    )}

                    <RevealElement>
                        <div
                            ref={paragraphRef}
                            className={`mb-0 tablet:mb-5 transition-all duration-300 columns-1 ${isTall ? "lg:columns-2" : ""} lg:gap-8`}
                            style={{
                                columnFill: "balance",
                                breakInside: "avoid",
                            }}
                        >
                            {data.description.map((desc: any, di: number) => {
                                if (desc.type !== "paragraph") return null;

                                const combinedText = desc.children
                                    .filter(
                                        (para: any) =>
                                            para.text &&
                                            para.text.trim() !== "",
                                    )
                                    .map((para: any) => {
                                        const text = para.text
                                            .replace(/\s+/g, " ")
                                            .trim();
                                        return para.bold
                                            ? `<span class="textChild bold sm:text-xl lg:text-sm xl:text-base 2xl:text-2xl mb-4 font-medium">${text}</span>`
                                            : text;
                                    })
                                    .join(" ");

                                return (
                                    <div
                                        key={di}
                                        className="textChild mb-4 lg:text-xs xl:text-base 2xl:text-2xl font-light"
                                        dangerouslySetInnerHTML={{
                                            __html: combinedText,
                                        }}
                                    />
                                );
                            })}
                        </div>
                        <div className="w-fit">
                            {data.ctaLink?.href && (
                                <AnimatedButton
                                    text={data.ctaLink.label}
                                    link={data.ctaLink.href}
                                    buttonClassName="!w-fit"
                                />
                            )}
                            <div className="h-4 md:h-24 xl:h-28"></div>
                        </div>
                    </RevealElement>
                </div>
            </div>

            {data.waveBgColor?.waveBgColor && (
                <BottomCurves
                    color={`${
                        data.waveBgColor?.waveBgColor ||
                        (data.hasGradient
                            ? "light grey"
                            : "rainbow purple to blue")
                    }`}
                    direction={data.waveDirection}
                />
            )}
        </section>
    );
}
